// File: src/utils/apiClient.js
// Description: Global API client with consistent authentication error handling

import { BASE_URL } from '../config';

// JWT helper functions
const decodeJwt = (token) => {
  try {
    const payload = token.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decodeURIComponent(escape(json)));
  } catch {
    try { 
      return JSON.parse(atob(token.split('.')[1])); 
    } catch { 
      return null; 
    }
  }
};

const isTokenExpired = (token) => {
  const data = decodeJwt(token);
  if (!data || !data.exp) return false; // if unknown, let server decide
  const now = Math.floor(Date.now() / 1000);
  return data.exp <= now;
};

/**
 * Enhanced fetch wrapper with automatic authentication error handling
 * @param {string} url - API endpoint URL (relative to BASE_URL or absolute)
 * @param {object} options - Fetch options (method, headers, body, etc.)
 * @param {object} authContext - { user, logout, navigate } from useContext and useNavigate
 * @returns {Promise<Response>} - Fetch response
 */
export const authenticatedFetch = async (url, options = {}, authContext = {}) => {
  const { user, logout, navigate } = authContext;
  
  // Build full URL if relative
  const fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url.startsWith('/') ? url : `/${url}`}`;
  
  // Prepare headers
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };
  
  // Add authorization header if user and token exist
  if (user?.token) {
    // Check token expiry before making request
    if (isTokenExpired(user.token)) {
      console.warn('Token expired locally, redirecting to login');
      if (logout && navigate) {
        logout();
        navigate('/login');
      }
      throw new Error('Your session has expired. Please sign in again.');
    }
    
    headers.Authorization = `Bearer ${user.token}`;
  }
  
  // Make the API call
  const response = await fetch(fullUrl, {
    ...options,
    headers
  });
  
  // Handle authentication errors
  if (response.status === 401 || response.status === 403) {
    console.warn(`Authentication error (${response.status}), redirecting to login`);
    
    if (logout && navigate) {
      logout();
      navigate('/login');
    }
    
    throw new Error('Your session is invalid or expired. Please sign in again.');
  }
  
  // Handle other error responses that might indicate auth issues
  if (!response.ok) {
    try {
      const errorData = await response.clone().json();
      const errorMessage = errorData?.detail || errorData?.message || '';
      
      // Check for auth-related error messages
      if (/Could not validate credentials|token|JWT|unauthorized/i.test(errorMessage)) {
        console.warn('Auth-related error detected in response, redirecting to login');
        
        if (logout && navigate) {
          logout();
          navigate('/login');
        }
        
        throw new Error('Your session is invalid or expired. Please sign in again.');
      }
    } catch (parseError) {
      // If we can't parse the error, continue with normal error handling
      console.log('Could not parse error response for auth check');
    }
  }
  
  return response;
};

/**
 * Simplified wrapper for authenticated API calls that handles JSON parsing
 * @param {string} url - API endpoint URL
 * @param {object} options - Fetch options
 * @param {object} authContext - { user, logout, navigate }
 * @returns {Promise<any>} - Parsed JSON response
 */
export const authenticatedRequest = async (url, options = {}, authContext = {}) => {
  const response = await authenticatedFetch(url, options, authContext);
  
  if (!response.ok) {
    // Try to parse error response
    let errorMessage = `HTTP ${response.status}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData?.detail || errorData?.message || errorMessage;
    } catch {
      try {
        errorMessage = await response.text() || errorMessage;
      } catch {
        // Use default message
      }
    }
    throw new Error(errorMessage);
  }
  
  // Parse successful response
  return await response.json();
};

/**
 * Hook-like function to create authenticated API methods with context
 * @param {object} authContext - { user, logout, navigate }
 * @returns {object} - Object with fetch and request methods
 */
export const createAuthenticatedAPI = (authContext) => ({
  fetch: (url, options = {}) => authenticatedFetch(url, options, authContext),
  request: (url, options = {}) => authenticatedRequest(url, options, authContext),
  
  // Common HTTP methods for convenience
  get: (url, options = {}) => authenticatedRequest(url, { ...options, method: 'GET' }, authContext),
  post: (url, data, options = {}) => authenticatedRequest(url, { 
    ...options, 
    method: 'POST', 
    body: JSON.stringify(data) 
  }, authContext),
  put: (url, data, options = {}) => authenticatedRequest(url, { 
    ...options, 
    method: 'PUT', 
    body: JSON.stringify(data) 
  }, authContext),
  delete: (url, options = {}) => authenticatedRequest(url, { ...options, method: 'DELETE' }, authContext),
});

export default authenticatedFetch;


